import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Role, UserStatus, type UserEventRelation } from '@regigov/contracts';
import { userAdminApi } from '../api/users';
import { AuditEventTable } from './AuditTrailPage';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

const RELATION_LABELS: Record<UserEventRelation, string> = { actor: 'By the user', subject: 'On the account', both: 'By the user, on the account' };

/** CER user administration: every account, searched by username, name, email or organisation. */
export function UserAdminListPage() {
  const [params, setParams] = useSearchParams();
  const current = { q: params.get('q') ?? '', role: params.get('role') ?? '', status: params.get('status') ?? '' };
  const after = params.get('after') ?? '';
  const [draft, setDraft] = useState(current);
  const key = params.toString();
  useEffect(() => setDraft(current), [key]);

  const role = Role.safeParse(current.role);
  const status = UserStatus.safeParse(current.status);
  const users = useQuery({
    queryKey: ['users', 'admin', key],
    queryFn: () => userAdminApi.list({ q: current.q || undefined, role: role.success ? role.data : undefined, status: status.success ? status.data : undefined, after: after || undefined }),
    staleTime: 0,
  });

  const apply = (d: typeof current, page?: string) => {
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(d)) if (v.trim()) next.set(k, v.trim());
    if (page) next.set('after', page);
    setParams(next);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    apply(draft);
  };

  return (
    <>
      <h1>Users</h1>
      <p className="muted">Every account in RegiGov: CER staff, participant users, applicants and testers, by username.</p>
      <form onSubmit={onSubmit}>
        <div className="row">
          <Field label="Search">
            <input value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} placeholder="Username, name, email or organisation" style={{ width: '24rem' }} />
          </Field>
          <Field label="Role">
            <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
              <option value="">Any</option>
              {Role.options.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
              <option value="">Any</option>
              {UserStatus.options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <button type="submit">Search</button>
          <button type="button" className="secondary" onClick={() => apply({ q: '', role: '', status: '' })}>
            Clear
          </button>
        </div>
      </form>
      <ProblemAlert error={users.error} />
      {users.isLoading && <p className="muted">Loading…</p>}
      {users.data && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Organisation</th>
                <th>Status</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {users.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    No users match.
                  </td>
                </tr>
              )}
              {users.data.items.map((u) => (
                <tr key={u.id} className={u.status === 'deactivated' ? 'muted' : undefined}>
                  <td>
                    <Link to={`/cer/users/${u.id}`}>{u.displayName}</Link>
                    <br />
                    <code className="muted">{u.username}</code>
                  </td>
                  <td>{u.email ? `${u.email} ${u.emailVerified ? '(verified)' : '(not verified)'}` : '—'}</td>
                  <td>
                    {u.roles.join(', ')}
                    {u.isTester && <span className="muted"> · tester</span>}
                  </td>
                  <td>{u.participantId ? <Link to={`/cer/participants/${u.participantId}`}>{u.participantName ?? u.participantId}</Link> : '—'}</td>
                  <td>
                    <StatusBadge status={u.status} />
                  </td>
                  <td>{formatDateTime(u.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row">
            {after && (
              <button type="button" className="secondary" onClick={() => apply(current)}>
                First page
              </button>
            )}
            {users.data.nextAfter !== null && (
              <button type="button" className="secondary" onClick={() => apply(current, users.data.nextAfter!)}>
                Next page
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** One account: its profile and its audit events (what the user did and what was done to the account). */
export function UserAdminDetailPage() {
  const { id = '' } = useParams();
  const [before, setBefore] = useState<number | undefined>(undefined);
  useEffect(() => setBefore(undefined), [id]);
  const user = useQuery({ queryKey: ['users', 'admin', 'detail', id], queryFn: () => userAdminApi.get(id), staleTime: 0 });
  const events = useQuery({ queryKey: ['users', 'admin', 'events', id, before], queryFn: () => userAdminApi.events(id, { before }), staleTime: 0, gcTime: 0, enabled: user.isSuccess });

  if (user.isLoading) return <p className="muted">Loading…</p>;
  if (user.error || !user.data) return <ProblemAlert error={user.error ?? new Error('Not found')} />;
  const u = user.data;

  return (
    <>
      <div className="row">
        <h1>{u.displayName}</h1>
        <StatusBadge status={u.status} />
        <Link to="/cer/users">All users</Link>
      </div>
      <DefinitionList
        items={[
          ['Username', <code key="u">{u.username}</code>],
          ['Roles', u.roles.join(', ')],
          ['Organisation', u.participantId ? <Link key="p" to={`/cer/participants/${u.participantId}`}>{u.participantName ?? u.participantId}</Link> : '—'],
          ['Email', u.email ? `${u.email} ${u.emailVerified ? '(verified)' : '(not verified)'}` : '—'],
          ['Sign-in', u.provider === 'local' ? 'Password' : 'Entra ID'],
          ['Tester', u.isTester ? 'Yes' : 'No'],
          ['Added', formatDateTime(u.createdAt)],
          ['Last signed in', u.lastSignInAt ? formatDateTime(u.lastSignInAt) : u.provider === 'local' ? 'Never' : 'Not recorded'],
          ['Password changed', u.passwordChangedAt ? formatDateTime(u.passwordChangedAt) : 'Not recorded'],
          ['Deactivated', u.deactivatedAt ? formatDateTime(u.deactivatedAt) : '—'],
          ...(u.testingOnboardedAt ? ([['Added by the Testing portal', formatDateTime(u.testingOnboardedAt)]] as Array<[string, string]>) : []),
          ['Last updated', formatDateTime(u.updatedAt)],
        ]}
      />
      <div className="row" style={{ marginTop: '1.5rem' }}>
        <h2>Audit events</h2>
        <Link to={`/cer/audit?actor=${encodeURIComponent(u.id)}`}>Open in the audit trail</Link>
      </div>
      <p className="muted">What this user did, and what was done to the account, newest first.</p>
      <ProblemAlert error={events.error} />
      {events.isLoading && <p className="muted">Loading…</p>}
      {events.data && (
        <>
          <AuditEventTable key={`${id}:${before ?? ""}`} items={events.data.items} extra={{ header: 'Relation', cell: (e) => RELATION_LABELS[e.relation] }} />
          <div className="row">
            {before !== undefined && (
              <button type="button" className="secondary" onClick={() => setBefore(undefined)}>
                Newest
              </button>
            )}
            {events.data.nextBefore !== null && (
              <button type="button" className="secondary" onClick={() => setBefore(events.data.nextBefore!)}>
                Older events
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
